import React, { useState } from 'react'

import { Minus, Plus } from '@factorialco/f0-react/icons/app'
import { F0Icon } from '@factorialco/f0-react'

type NumberCounterProps = {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  decreaseLabel?: string
  increaseLabel?: string
}

export const NumberCounter: React.FC<NumberCounterProps> = ({
  value,
  onChange,
  min = 1,
  max,
  decreaseLabel = 'Decrease',
  increaseLabel = 'Increase',
}) => {
  const [draft, setDraft] = useState<string>(String(value))
  const [editing, setEditing] = useState(false)

  const atMin = value <= min
  const atMax = max !== undefined && value >= max

  const handleDecrement = () => {
    if (!atMin) onChange(value - 1)
  }

  const handleIncrement = () => {
    if (!atMax) onChange(value + 1)
  }

  const handleFocus = () => {
    setDraft(String(value))
    setEditing(true)
  }

  const handleBlur = () => {
    setEditing(false)
    const parsed = parseInt(draft, 10)
    if (isNaN(parsed) || draft.trim() === '') {
      onChange(value)
      setDraft(String(value))
      return
    }
    const clamped = Math.max(min, max !== undefined ? Math.min(max, parsed) : parsed)
    onChange(clamped)
    setDraft(String(clamped))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    }
  }

  // Keep draft in sync when value changes externally
  const displayValue = editing ? draft : String(value)

  return (
    <div className="inline-flex items-center rounded-sm border border-solid border-f1-border overflow-hidden h-8">
      <button
        type="button"
        aria-label={decreaseLabel}
        disabled={atMin}
        onClick={handleDecrement}
        className="flex items-center justify-center w-8 h-full border-none bg-transparent cursor-pointer hover:bg-f1-background-secondary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <F0Icon icon={Minus} size="md" color={atMin ? 'default' : 'bold'} />
      </button>
      <div className="w-px h-full bg-f1-border" />
      <input
        role="textbox"
        value={displayValue}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="h-full w-16 text-center border-none outline-none bg-transparent text-md font-medium"
      />
      <div className="w-px h-full bg-f1-border" />
      <button
        type="button"
        aria-label={increaseLabel}
        disabled={atMax}
        onClick={handleIncrement}
        className="flex items-center justify-center w-8 h-full border-none bg-transparent cursor-pointer hover:bg-f1-background-secondary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <F0Icon icon={Plus} size="md" color={atMax ? 'default' : 'bold'} />
      </button>
    </div>
  )
}
